import { useState, useEffect } from 'react';
import { supabase } from '../../../../supabaseClient';

/**
 * Hook to load all form lookup data from the database
 * This includes departments, locations, event categories, etc.
 */
export const useFormData = () => {
  const [formData, setFormData] = useState({
    departments: [],
    locations: [],
    eventCategories: [],
    eventTypes: [],
    aircraftTypes: [],
    priorityLevels: [],
    riskSeverity: [],
    riskLikelihood: [],
            stations: [
              { id: 'station-1', station_name: 'Head Office - Chonburi', station_code: 'HO', city_name: 'Chonburi' },
              { id: 'station-2', station_name: 'UTP - Rayong', station_code: 'UTP', city_name: 'Rayong' },
              { id: 'station-3', station_name: 'KBV - Krabi', station_code: 'KBV', city_name: 'Krabi' },
              { id: 'station-4', station_name: 'HKT - Phuket', station_code: 'HKT', city_name: 'Phuket' },
              { id: 'station-5', station_name: 'BKK - Samutprakarn', station_code: 'BKK', city_name: 'Samutprakarn' },
              { id: 'station-6', station_name: 'DMK - Bangkok', station_code: 'DMK', city_name: 'Bangkok' }
            ],
            vendors: []
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadFormData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('Loading SMS form lookup data...');

        // Load all lookup data in parallel for better performance
        const [
          departmentsResult,
          locationsResult,
          categoriesResult,
          typesResult,
          aircraftTypesResult,
          priorityLevelsResult,
          severityResult,
          likelihoodResult,
          stationsResult,
          vendorsResult
        ] = await Promise.all([
          // Dynamic departments
          supabase
            .from('sms_departments')
            .select('*')
            .eq('is_active', true)
            .order('dept_name'),

          // Internal locations
          supabase
            .from('sms_locations')
            .select('*')
            .eq('is_active', true)
            .order('location_name'),

          // Event categories
          supabase
            .from('sms_event_categories')
            .select('*')
            .eq('is_active', true)
            .order('category_name'),

          // Event types
          supabase
            .from('sms_event_types')
            .select('*')
            .eq('is_active', true)
            .order('type_name'),

          // Aircraft types
          supabase
            .from('sms_aircraft_types')
            .select('*')
            .eq('is_active', true)
            .order('type_name'),

          // Priority levels
          supabase
            .from('sms_priority_levels')
            .select('*')
            .eq('is_active', true)
            .order('priority_level'),

          // Risk severity levels
          supabase
            .from('sms_risk_severity')
            .select('*')
            .eq('is_active', true)
            .order('severity_level'),

          // Risk likelihood levels
          supabase
            .from('sms_risk_likelihood')
            .select('*')
            .eq('is_active', true)
            .order('likelihood_level'),

          // SMS operational stations
          supabase
            .from('sms_station')
            .select('id, station_name, station_code, city_name, country')
            .eq('is_active', true)
            .order('station_name'),

          // Approved vendors
          supabase
            .from('approved_vendors')
            .select('id, supplier_name, supplier_code')
            .eq('status', 'Active')
            .order('supplier_name')
        ]);

        // Check for errors in any of the queries
        const results = [
          departmentsResult,
          locationsResult,
          categoriesResult,
          typesResult,
          aircraftTypesResult,
          priorityLevelsResult,
          severityResult,
          likelihoodResult,
          stationsResult,
          vendorsResult
        ];

        const errorResult = results.find(result => result.error);
        if (errorResult) {
          throw errorResult.error;
        }

        // Set the loaded data
        setFormData({
          departments: departmentsResult.data || [],
          locations: locationsResult.data || [],
          eventCategories: categoriesResult.data || [],
          eventTypes: typesResult.data || [],
          aircraftTypes: aircraftTypesResult.data || [],
          priorityLevels: priorityLevelsResult.data || [],
          riskSeverity: severityResult.data || [],
          riskLikelihood: likelihoodResult.data || [],
          stations: stationsResult.data || [],
          vendors: vendorsResult.data || []
        });

        console.log('SMS form data loaded successfully:', {
          departments: departmentsResult.data?.length || 0,
          locations: locationsResult.data?.length || 0,
          eventCategories: categoriesResult.data?.length || 0,
          eventTypes: typesResult.data?.length || 0,
          aircraftTypes: aircraftTypesResult.data?.length || 0,
          priorityLevels: priorityLevelsResult.data?.length || 0,
          stations: stationsResult.data?.length || 0,
          vendors: vendorsResult.data?.length || 0
        });

        // Debug: Log the actual data for problematic dropdowns
        console.log('DEBUG - Departments data:', departmentsResult.data);
        console.log('DEBUG - Locations data:', locationsResult.data);
        console.log('DEBUG - Event Categories data:', categoriesResult.data);
        
        // Debug: Log any errors from the queries
        console.log('DEBUG - Departments error:', departmentsResult.error);
        console.log('DEBUG - Locations error:', locationsResult.error);
        console.log('DEBUG - Event Categories error:', categoriesResult.error);

      } catch (err) {
        console.error('Error loading SMS form data:', err);
        console.error('Full error details:', err);
        
        // If SMS tables don't exist, provide fallback data
        if (err.message?.includes('relation') && err.message?.includes('does not exist')) {
          console.warn('SMS tables not found - using fallback data. Please run the database setup script.');
          setFormData({
            departments: [
              { id: 'temp-1', dept_name: 'Line Maintenance', dept_code: 'LMX' },
              { id: 'temp-2', dept_name: 'Heavy Maintenance', dept_code: 'HMX' },
              { id: 'temp-3', dept_name: 'Quality Assurance', dept_code: 'QAS' },
              { id: 'temp-4', dept_name: 'Engineering', dept_code: 'ENG' }
            ],
            locations: [
              { id: 'temp-1', location_name: 'Hangar 1', location_code: 'HGR01' },
              { id: 'temp-2', location_name: 'Hangar 2', location_code: 'HGR02' },
              { id: 'temp-3', location_name: 'Line Maintenance Ramp', location_code: 'LMRP' },
              { id: 'temp-4', location_name: 'Component Workshop', location_code: 'WSHP' }
            ],
            eventCategories: [
              { id: 'temp-1', category_name: 'Maintenance Operations', category_code: 'MNT', requires_aircraft_info: true },
              { id: 'temp-2', category_name: 'Ground Operations', category_code: 'GRD', requires_aircraft_info: false },
              { id: 'temp-3', category_name: 'Health, Safety & Environment', category_code: 'HSE', requires_aircraft_info: false },
              { id: 'temp-4', category_name: 'Equipment', category_code: 'EQP', requires_aircraft_info: false }
            ],
            eventTypes: [
              { id: 'temp-1', type_name: 'Tool-related Incident', category_id: 'temp-1', type_code: 'TOOL' },
              { id: 'temp-2', type_name: 'Parts/Component Issue', category_id: 'temp-1', type_code: 'PART' },
              { id: 'temp-3', type_name: 'Personal Injury', category_id: 'temp-3', type_code: 'INJR' },
              { id: 'temp-4', type_name: 'Near Miss', category_id: 'temp-3', type_code: 'NEAR' }
            ],
            aircraftTypes: [
              { id: 'temp-1', type_name: 'Airbus A320', type_code: 'A320' },
              { id: 'temp-2', type_name: 'Boeing 737', type_code: 'B737' }
            ],
            priorityLevels: [
              { id: 'temp-1', priority_name: 'Critical', priority_level: 1 },
              { id: 'temp-2', priority_name: 'High', priority_level: 2 },
              { id: 'temp-3', priority_name: 'Medium', priority_level: 3 }
            ],
            riskSeverity: [],
            riskLikelihood: [],
            stations: [],
            vendors: []
          });
          setError('SMS database tables not found. Using fallback data. Please run the database setup script.');
        } else {
          setError(err.message || 'Failed to load form data');
        }
      } finally {
        setLoading(false);
      }
    };

    loadFormData();
  }, []);

  return { ...formData, loading, error };
};

/**
 * Hook to get filtered event types based on selected category
 */
export const useEventTypes = (categoryId, allEventTypes) => {
  return allEventTypes.filter(type => type.category_id === categoryId);
};

/**
 * Hook to check if event category requires aircraft information
 */
export const useRequiresAircraftInfo = (categoryId, allEventCategories) => {
  const category = allEventCategories.find(cat => cat.id === categoryId);
  return category?.requires_aircraft_info || false;
};

/**
 * Hook to get department escalation hours
 */
export const useDepartmentInfo = (departmentId, allDepartments) => {
  const department = allDepartments.find(dept => dept.id === departmentId);
  return {
    department,
    escalationHours: department?.escalation_hours || 24,
    isSafetyCritical: department?.is_safety_critical || false
  };
};
